import pLimit from "p-limit";

import type { FileItem } from "../FileGrid";
import { authHeaders } from "./api";
import { TransferTask } from "./transferQueue";
import { encodeKey, joinKey, validName } from "./utils";

const LIST_CACHE_TTL_MS = 60_000;
const listCache = new Map<
  string,
  { expiresAt: number; files?: FileItem[]; promise?: Promise<FileItem[]> }
>();

function visibleFiles(files: FileItem[]) {
  return files.filter(
    (file) =>
      file.key !== "_$flaredrive$" && !file.key.startsWith("_$flaredrive$/")
  );
}

export function invalidatePath(path: string) {
  listCache.delete(path);
}

export async function fetchPath(path: string, options?: { force?: boolean }) {
  const cached = listCache.get(path);
  const now = Date.now();

  if (!options?.force) {
    if (cached?.files && cached.expiresAt > now) return cached.files;
    if (cached?.promise) return cached.promise;
  }

  const searchParams = new URLSearchParams({ prefix: path });
  const promise = fetch(`/api/list?${searchParams}`, {
    cache: options?.force ? "reload" : "default",
  })
    .then(async (res) => {
      if (!res.ok) throw new Error((await res.text()) || "Failed to fetch");
      const data = await res.json<{ files: FileItem[] }>();
      const files = visibleFiles(data.files);
      listCache.set(path, {
        files,
        expiresAt: Date.now() + LIST_CACHE_TTL_MS,
      });
      return files;
    })
    .catch((error) => {
      listCache.delete(path);
      throw error;
    });

  listCache.set(path, { promise, expiresAt: now + LIST_CACHE_TTL_MS });
  return promise;
}

const THUMBNAIL_SIZE = 144;

export async function generateThumbnail(file: File) {
  const canvas = document.createElement("canvas");
  canvas.width = THUMBNAIL_SIZE;
  canvas.height = THUMBNAIL_SIZE;
  const ctx = canvas.getContext("2d")!;

  if (file.type.startsWith("image/")) {
    const objectUrl = URL.createObjectURL(file);
    try {
      const image = await new Promise<HTMLImageElement>((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error("Image load failed"));
        image.src = objectUrl;
      });
      ctx.drawImage(image, 0, 0, THUMBNAIL_SIZE, THUMBNAIL_SIZE);
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  } else if (file.type === "video/mp4") {
    const objectUrl = URL.createObjectURL(file);
    try {
      const video = await new Promise<HTMLVideoElement>(
        async (resolve, reject) => {
          const video = document.createElement("video");
          video.muted = true;
          video.src = objectUrl;
          setTimeout(() => reject(new Error("Video load timeout")), 2000);
          await video.play();
          video.pause();
          video.currentTime = 0;
          resolve(video);
        }
      );
      ctx.drawImage(video, 0, 0, THUMBNAIL_SIZE, THUMBNAIL_SIZE);
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  } else if (file.type === "application/pdf") {
    const objectUrl = URL.createObjectURL(file);
    try {
      const pdfjsLib = await import(
        // @ts-ignore
        "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.4.168/pdf.min.mjs"
      );
      pdfjsLib.GlobalWorkerOptions.workerSrc =
        "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.4.168/pdf.worker.min.mjs";
      const pdf = await pdfjsLib.getDocument(objectUrl).promise;
      const page = await pdf.getPage(1);
      const { width, height } = page.getViewport({ scale: 1 });
      const scale = THUMBNAIL_SIZE / Math.max(width, height);
      const viewport = page.getViewport({ scale });
      const renderContext = { canvasContext: ctx, viewport };
      await page.render(renderContext).promise;
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  }

  const thumbnailBlob = await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error("Thumbnail encoding failed"));
    })
  );

  return thumbnailBlob;
}
export async function blobDigest(blob: Blob) {
  const digest = await crypto.subtle.digest("SHA-1", await blob.arrayBuffer());
  const digestArray = Array.from(new Uint8Array(digest));
  const digestHex = digestArray
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return digestHex;
}

export const SIZE_LIMIT = 100 * 1000 * 1000; // 100MB

function xhrFetch(
  url: RequestInfo | URL,
  requestInit: RequestInit & {
    onUploadProgress?: (progressEvent: ProgressEvent) => void;
  }
) {
  return new Promise<Response>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.upload.onprogress = requestInit.onUploadProgress ?? null;
    xhr.open(
      requestInit.method ?? "GET",
      url instanceof Request ? url.url : url
    );
    const headers = new Headers(requestInit.headers);
    headers.forEach((value, key) => xhr.setRequestHeader(key, value));
    xhr.onload = () => {
      const headers = xhr
        .getAllResponseHeaders()
        .trim()
        .split("\r\n")
        .reduce((acc, header) => {
          const [key, value] = header.split(": ");
          acc[key] = value;
          return acc;
        }, {} as Record<string, string>);
      resolve(new Response(xhr.responseText, { status: xhr.status, headers }));
    };
    xhr.onerror = reject;
    if (
      requestInit.body instanceof Blob ||
      typeof requestInit.body === "string"
    ) {
      xhr.send(requestInit.body);
    }
  });
}

export async function multipartUpload(
  key: string,
  file: File,
  options?: {
    headers?: Record<string, string>;
    onUploadProgress?: (progressEvent: {
      loaded: number;
      total: number;
    }) => void;
  }
) {
  const headers = options?.headers || {};
  headers["content-type"] = file.type;

  const uploadResponse = await fetch(`/api/file/${encodeKey(key)}?uploads`, {
    headers: authHeaders(headers),
    method: "POST",
  });
  if (!uploadResponse.ok) throw new Error(await uploadResponse.text());
  const { uploadId } = await uploadResponse.json<{ uploadId: string }>();
  const totalChunks = Math.ceil(file.size / SIZE_LIMIT);

  const limit = pLimit(2);
  const parts = Array.from({ length: totalChunks }, (_, i) => i + 1);
  const partsLoaded = Array.from({ length: totalChunks + 1 }, () => 0);
  const promises = parts.map((i) =>
    limit(async () => {
      const chunk = file.slice((i - 1) * SIZE_LIMIT, i * SIZE_LIMIT);
      const searchParams = new URLSearchParams({
        partNumber: i.toString(),
        uploadId,
      });
      const uploadUrl = `/api/file/${encodeKey(key)}?${searchParams}`;
      if (i === limit.concurrency)
        await new Promise((resolve) => setTimeout(resolve, 1000));

      const uploadPart = () =>
        xhrFetch(uploadUrl, {
          method: "PUT",
          headers: authHeaders(headers),
          body: chunk,
          onUploadProgress: (progressEvent) => {
            partsLoaded[i] = progressEvent.loaded;
            options?.onUploadProgress?.({
              loaded: partsLoaded.reduce((a, b) => a + b),
              total: file.size,
            });
          },
        });

      const retryReducer = (acc: Promise<Response>) =>
        acc
          .then((res) => {
            const retryAfter = res.headers.get("retry-after");
            if (!retryAfter) return res;
            return uploadPart();
          })
          .catch(uploadPart);
      const response = await [1, 2].reduce(retryReducer, uploadPart());
      if (!response.ok) throw new Error(await response.text());
      return { partNumber: i, etag: response.headers.get("etag")! };
    })
  );
  const uploadedParts = await Promise.all(promises);
  const completeParams = new URLSearchParams({ uploadId });
  const response = await fetch(`/api/file/${encodeKey(key)}?${completeParams}`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ parts: uploadedParts }),
  });
  if (!response.ok) throw new Error(await response.text());
  return response;
}

export async function copyPaste(source: string, target: string, move = false) {
  const response = await fetch("/api/copy", {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ source, target, move }),
  });
  if (!response.ok) throw new Error(await response.text());
}

export async function createFolder(cwd: string) {
  try {
    const folderName = window.prompt("Folder name");
    if (!folderName) return false;
    const cleanName = folderName.trim();
    if (!validName(cleanName)) {
      window.alert("Invalid folder name");
      return false;
    }
    const folderKey = joinKey(cwd, cleanName);
    const uploadUrl = `/api/folder/${encodeKey(folderKey)}`;
    const response = await fetch(uploadUrl, {
      method: "PUT",
      headers: authHeaders(),
    });
    if (!response.ok) throw new Error(await response.text());
    return true;
  } catch (error) {
    window.alert(
      error instanceof Error ? error.message : "Create folder failed"
    );
    return false;
  }
}

export async function processTransferTask({
  task,
  onTaskProgress,
}: {
  task: TransferTask;
  onTaskProgress?: (event: { loaded: number; total: number }) => void;
}) {
  const { remoteKey, file } = task;
  if (task.type !== "upload" || !file) throw new Error("Invalid task");
  let thumbnailDigest = null;

  if (
    file.type.startsWith("image/") ||
    file.type === "video/mp4" ||
    file.type === "application/pdf"
  ) {
    try {
      const thumbnailBlob = await generateThumbnail(file);
      const digestHex = await blobDigest(thumbnailBlob);

      const thumbnailUploadUrl = `/api/file/_$flaredrive$/thumbnails/${digestHex}.png`;
      try {
        await fetch(thumbnailUploadUrl, {
          method: "PUT",
          headers: authHeaders(),
          body: thumbnailBlob,
        });
        thumbnailDigest = digestHex;
      } catch {}
    } catch {}
  }

  const headers: { "fd-thumbnail"?: string } = {};
  if (thumbnailDigest) headers["fd-thumbnail"] = thumbnailDigest;
  if (file.size >= SIZE_LIMIT) {
    return await multipartUpload(remoteKey, file, {
      headers,
      onUploadProgress: onTaskProgress,
    });
  } else {
    const uploadUrl = `/api/file/${encodeKey(remoteKey)}`;
    const response = await xhrFetch(uploadUrl, {
      method: "PUT",
      headers: authHeaders(headers),
      body: file,
      onUploadProgress: onTaskProgress,
    });
    if (!response.ok) throw new Error(await response.text());
    return response;
  }
}
